import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ClientOnly, useHydrated, Link } from '@tanstack/react-router';
import { Phone, Users, Clock, User, Settings } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useIsAdmin } from '@/hooks/useAccount';
import { useQuery } from '@tanstack/react-query';
import { accountQueries } from '@/services/accountApi';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { getLocationHash, setLocationHash } from '@/lib/isomorphic';
import PhoneDialer from './PhoneDialer';
import ContactsPanel from './ContactsPanel';
import HistoryPanel from './HistoryPanel';
import SettingsPanel from './SettingsPanel';
import ContactDetailPanel, { ContactDetailState } from './ContactDetailPanel';

interface PhoneAppProps {
  className?: string;
}

const PhoneApp: React.FC<PhoneAppProps> = ({ className }) => {
  const { t } = useTranslation(['app', 'common']);
  const isHydrated = useHydrated();
  const isDesktop = useBreakpoint('lg');
  const { data: accountData, isLoading: isLoadingAccount } = useQuery(accountQueries.me());
  const isAdmin = useIsAdmin();

  const validTabs = React.useMemo(() => {
    const base = isDesktop ? ['contacts', 'recents'] : ['dialer', 'contacts', 'recents'];
    if (isAdmin) return [...base, 'settings'];
    return base;
  }, [isDesktop, isAdmin]);

  const getInitialTab = React.useCallback(() => {
    if (!isHydrated) return 'dialer';
    const hash = getLocationHash().slice(1);
    if (hash === 'dialer' && isDesktop) return 'contacts';
    if (hash && validTabs.includes(hash)) return hash;
    return isDesktop ? 'contacts' : 'dialer';
  }, [isHydrated, isDesktop, validTabs]);

  const [activeTab, setActiveTabState] = useState<string>('dialer');
  const [prefillNumber, setPrefillNumber] = useState<string | null>(null);
  const [contactDetail, setContactDetail] = useState<ContactDetailState | null>(null);

  useEffect(() => {
    if (isHydrated) setActiveTabState(getInitialTab());
  }, [isHydrated, getInitialTab]);

  const setActiveTab = useCallback((tab: string) => {
    setActiveTabState(tab);
    setLocationHash(tab);
  }, []);

  const account = accountData?.account;
  const isVerified = account?.verified ?? true;

  const handleOpenContact = useCallback((detail: ContactDetailState) => {
    setContactDetail(detail);
  }, []);

  const handleCloseContact = useCallback(() => {
    setContactDetail(null);
  }, []);

  const handleCallFromDetail = useCallback((phoneNumber: string) => {
    setContactDetail(null);
    if (!isDesktop) setActiveTab('dialer');
    setPrefillNumber(phoneNumber);
  }, [isDesktop, setActiveTab]);

  const handleContactUpdated = useCallback(() => {
    // Detail panel invalidates queries itself
  }, []);

  const handleCall = useCallback((number: string) => {
    if (!isDesktop) setActiveTab('dialer');
    setPrefillNumber(number);
  }, [isDesktop, setActiveTab]);

  // Shared panel rendering — used by both layouts
  const listPanel = (
    <>
      {activeTab === 'contacts' && (
        <ContactsPanel onOpenContact={handleOpenContact} onCall={handleCall} className="h-full" />
      )}
      {activeTab === 'recents' && (
        <HistoryPanel onOpenContact={handleOpenContact} onCall={handleCall} className="h-full" />
      )}
      {activeTab === 'settings' && <SettingsPanel />}
    </>
  );

  const contactDetailPanel = contactDetail ? (
    <ContactDetailPanel
      detail={contactDetail}
      onBack={handleCloseContact}
      onCall={handleCallFromDetail}
      onContactUpdated={handleContactUpdated}
    />
  ) : null;

  const dialer = (
    <PhoneDialer
      className="border-0 shadow-none"
      prefillNumber={prefillNumber}
      onPrefillConsumed={() => setPrefillNumber(null)}
    />
  );

  return (
    <ClientOnly fallback={null}>
    {isDesktop ? (
      /* ------------------------------------------------------------------ */
      /* Desktop layout                                                       */
      /* ------------------------------------------------------------------ */
      <div className={cn("flex flex-1 bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-6 min-h-0", className)}>
        {/* Sidebar */}
        <div data-desktop-sidebar className="w-20 bg-primary rounded-2xl flex flex-col items-center py-8 gap-4 shadow-lg">
          <button
            onClick={() => { setActiveTab('contacts'); setContactDetail(null); }}
            className={cn("w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all",
              activeTab === 'contacts' ? "bg-background text-primary shadow-md scale-105" : "text-primary-foreground hover:bg-primary-foreground/10 hover:scale-105"
            )}
          >
            <Users className="w-5 h-5" />
            <span className="text-[10px] font-medium">{t('contacts.title')}</span>
          </button>
          <button
            onClick={() => { setActiveTab('recents'); setContactDetail(null); }}
            className={cn("w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all",
              activeTab === 'recents' ? "bg-background text-primary shadow-md scale-105" : "text-primary-foreground hover:bg-primary-foreground/10 hover:scale-105"
            )}
          >
            <Clock className="w-5 h-5" />
            <span className="text-[10px] font-medium">{t('navigation.recents')}</span>
          </button>
          <Link
            to="/account"
            className={cn("w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all no-underline",
              "text-primary-foreground hover:bg-primary-foreground/10 hover:scale-105"
            )}
          >
            <User className="w-5 h-5" />
            <span className="text-[10px] font-medium">{t('navigation.account')}</span>
          </Link>
          {isAdmin && (
            <button
              onClick={() => { setActiveTab('settings'); setContactDetail(null); }}
              className={cn("w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all",
                activeTab === 'settings' ? "bg-background text-primary shadow-md scale-105" : "text-primary-foreground hover:bg-primary-foreground/10 hover:scale-105"
              )}
            >
              <Settings className="w-5 h-5" />
              <span className="text-[10px] font-medium">{t('navigation.settings')}</span>
            </button>
          )}
        </div>

        {/* Main content */}
        <div className="flex-1 flex ml-6 bg-background rounded-2xl shadow-2xl overflow-hidden border border-border/50 min-h-0">
          {/* Left panel — list */}
          <div className="w-[35%] border-r border-border overflow-hidden relative h-full flex flex-col min-h-0">
            {listPanel}
          </div>

          {/* Right panel — contact detail OR dialer */}
          <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-background to-muted/20 overflow-hidden">
            {contactDetailPanel ? (
              <div className="w-full h-full">
                {contactDetailPanel}
              </div>
            ) : dialer}
          </div>
        </div>
      </div>
    ) : (
      /* ------------------------------------------------------------------ */
      /* Mobile layout                                                        */
      /* ------------------------------------------------------------------ */
      <div className="flex-1 flex flex-col min-h-0">
        {!isLoadingAccount && !isVerified && (
          <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium dark:bg-amber-950/30 dark:border-amber-900/50 dark:text-amber-200">
            {t("account.verificationWarning")}
          </div>
        )}
        <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); setContactDetail(null); }} className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-hidden min-h-0 relative">
            <TabsContent value="dialer" forceMount className="h-full flex items-center justify-center data-[state=inactive]:hidden">
              {dialer}
            </TabsContent>

            <TabsContent value="contacts" forceMount className="h-full data-[state=inactive]:hidden">
              <ContactsPanel onOpenContact={handleOpenContact} onCall={handleCall} className="h-full" />
            </TabsContent>

            <TabsContent value="recents" forceMount className="h-full data-[state=inactive]:hidden">
              <HistoryPanel onOpenContact={handleOpenContact} onCall={handleCall} className="h-full" />
            </TabsContent>

            {isAdmin && (
              <TabsContent value="settings" forceMount className="h-full data-[state=inactive]:hidden">
                <SettingsPanel />
              </TabsContent>
            )}

            {/* Contact detail slides over the current tab on mobile */}
            {contactDetailPanel && (
              <div className="absolute inset-0 z-20 bg-background">
                {contactDetailPanel}
              </div>
            )}
          </div>

          <div className="border-t border-border/50 bg-background/95 backdrop-blur-xl pb-safe">
            <TabsList className={cn("grid h-20! w-full bg-transparent rounded-none border-0 p-2 gap-1", isAdmin ? "grid-cols-5" : "grid-cols-4")}>
              <TabsTrigger value="dialer" className={cn("flex-col gap-1.5 rounded-xl transition-all", "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md", "data-[state=inactive]:hover:bg-muted")}>
                <Phone className="w-5 h-5" />
                <span className="text-xs font-medium">{t('dialer.phone')}</span>
              </TabsTrigger>
              <TabsTrigger value="contacts" className={cn("flex-col gap-1.5 rounded-xl transition-all", "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md", "data-[state=inactive]:hover:bg-muted")}>
                <Users className="w-5 h-5" />
                <span className="text-xs font-medium">{t('contacts.savedContacts')}</span>
              </TabsTrigger>
              <TabsTrigger value="recents" className={cn("flex-col gap-1.5 rounded-xl transition-all", "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md", "data-[state=inactive]:hover:bg-muted")}>
                <Clock className="w-5 h-5" />
                <span className="text-xs font-medium">{t('navigation.recents')}</span>
              </TabsTrigger>
              <Link
                to="/account"
                className={cn(
                  "flex flex-col gap-1.5 items-center justify-center rounded-xl transition-all no-underline text-muted-foreground",
                  "hover:bg-muted hover:text-foreground"
                )}
              >
                <User className="w-5 h-5" />
                <span className="text-xs font-medium">{t('navigation.account')}</span>
              </Link>
              {isAdmin && (
                <TabsTrigger value="settings" className={cn("flex-col gap-1.5 rounded-xl transition-all", "data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-md", "data-[state=inactive]:hover:bg-muted")}>
                  <Settings className="w-5 h-5" />
                  <span className="text-xs font-medium">{t('navigation.settings')}</span>
                </TabsTrigger>
              )}
            </TabsList>
          </div>
        </Tabs>
      </div>
    )}
    </ClientOnly>
  );
};

export default PhoneApp;
